<template>
    <Layout>
        <b-card id="form">
            <div class="card-body">
                <h4 class="mb-4">Perfil e Assinatura</h4>

                <!-- Assinatura Ativa -->
                <b-card-body v-if="currentPlan && isSubscriptionActive" class="subscription-active">
                    <div class="subscription-header text-center mb-4">
                        <div class="subscription-status-badge" :class="{'trial-badge': currentPlan.type === 'TRIAL'}">
                            <i class="fa fa-check-circle text-success" v-if="currentPlan.type !== 'TRIAL'"></i>
                            <i class="fa fa-gift text-warning" v-else></i>
                            <span class="ml-2">{{ currentPlan.type === 'TRIAL' ? 'Trial Ativo' : 'Assinatura Ativa' }}</span>
                        </div>
                        <h3 class="mt-3 text-primary">{{ currentPlan.title }}</h3>
                        <p class="text-muted" v-if="currentPlan.type !== 'TRIAL'">Seu plano está funcionando perfeitamente!</p>
                        <p class="text-muted" v-else>
                            <strong>Trial de {{ currentPlan.trial_days }} dias</strong> - 
                            <span v-if="trialExpiresAt">Expira em {{ trialExpiresAt }}</span>
                        </p>
                    </div>

                    <b-row class="subscription-details">
                        <b-col md="8" class="mx-auto">
                            <div class="plan-card" :class="{'trial-card': currentPlan.type === 'TRIAL'}">
                                <div class="plan-header">
                                    <div class="plan-icon" :class="{'trial-icon': currentPlan.type === 'TRIAL'}">
                                        <i class="fa fa-star" v-if="currentPlan.type !== 'TRIAL'"></i>
                                        <i class="fa fa-gift" v-else></i>
                                    </div>
                                    <div class="plan-info">
                                        <h4 class="plan-title">{{ currentPlan.title }}</h4>
                                        <div class="plan-description" v-html="currentPlan.description"></div>
                                    </div>
                                </div>
                          
                                <div class="plan-pricing" v-if="currentPlan.type !== 'TRIAL'">
                                    <div class="price-label">Valor Mensal</div>
                                    <div class="price-value">R$ {{ formatCurrency(currentPlan.value) }}</div>
                                    <div class="price-period">por mês</div>
                                </div>

                                <div class="trial-pricing" v-else>
                                    <div class="trial-label">Período de Teste</div>
                                    <div class="trial-value">GRÁTIS</div>
                                    <div class="trial-period">{{ currentPlan.trial_days }} dias</div>
                                </div>

                                <div class="plan-actions">
                                    <b-button 
                                        variant="outline-danger" 
                                        size="md" 
                                        @click="showCancelModal = true"
                                        class="cancel-btn"
                                        v-if="currentPlan.type !== 'TRIAL'"
                                    >
                                        <i class="fa fa-ban mr-2"></i> Cancelar Assinatura
                                    </b-button>
                                    
                                    <b-button 
                                        variant="success" 
                                        size="md" 
                                        @click="showReactivateModal = true"
                                        class="upgrade-btn"
                                        v-else
                                    >
                                        <i class="fa fa-arrow-up mr-2"></i> Fazer Upgrade
                                    </b-button>
                                </div>
                            </div>
                        </b-col>
                    </b-row>
                </b-card-body>

                <!-- Assinatura Inativa - Opção de Reativação -->
                <b-card-body v-else-if="!isSubscriptionActive" class="subscription-inactive">
                    <div class="text-center mb-4">
                        <div class="status-icon warning">
                            <i class="fa fa-exclamation-triangle"></i>
                        </div>
                        <h4 class="mt-3 text-warning">Assinatura Inativa</h4>
                        <p class="text-muted">Sua assinatura está inativa. Ative para continuar aproveitando os benefícios da plataforma.</p>
                    </div>
                    
                    <b-row>
                        <b-col md="8" class="mx-auto text-center">
                            <b-button variant="success" size="lg" class="reactivate-btn" @click="showReactivateModal = true">
                                <i class="fa fa-refresh mr-2"></i> Ativar Assinatura
                            </b-button>
                            
                            <div class="mt-3">
                                <a href="https://wa.me/5511939078700?text=Olá! Preciso de ajuda com minha assinatura na plataforma Lummefy." 
                                   target="_blank" 
                                   class="btn btn-outline-success">
                                    <i class="fab fa-whatsapp mr-2"></i>
                                    Falar com Especialista
                                </a>
                            </div>
                        </b-col>
                    </b-row>
                </b-card-body>

                <!-- Sem Plano - Opção de Assinatura -->
                <b-card-body v-else class="subscription-none">
                    <div class="text-center mb-4">
                        <div class="status-icon info">
                            <i class="fa fa-info-circle"></i>
                        </div>
                        <h4 class="mt-3 text-info">Nenhum Plano Ativo</h4>
                        <p class="text-muted">Você ainda não possui um plano ativo. Escolha um plano para começar a aproveitar todos os benefícios.</p>
                    </div>
                    
                    <b-row>
                        <b-col md="8" class="mx-auto text-center">
                            <!-- Opção de Trial -->
                            <div class="trial-option mb-4">
                                <div class="trial-card">
                                    <div class="trial-header">
                                        <div class="trial-icon">
                                            <i class="fa fa-gift"></i>
                                        </div>
                                        <div class="trial-info">
                                            <h5 class="trial-title">Teste Grátis</h5>
                                            <p class="trial-subtitle">7 dias para experimentar</p>
                                        </div>
                                    </div>
                                    <div class="trial-description">
                                        <p>Experimente todas as funcionalidades da plataforma por 7 dias sem compromisso!</p>
                                    </div>
                                    <b-button 
                                        variant="success" 
                                        size="lg" 
                                        class="trial-btn"
                                        @click="startTrial"
                                    >
                                        <i class="fa fa-play mr-2"></i> Começar Trial Grátis
                                    </b-button>
                                </div>
                            </div>

                            <!-- Divisor -->
                            <div class="divider">
                                <span>ou</span>
                            </div>

                            <!-- Opção de Plano Pago -->
                            <div class="paid-plan-option mt-4">
                                <b-button variant="primary" size="lg" class="choose-plan-btn" @click="showReactivateModal = true">
                                    <i class="fa fa-plus mr-2"></i> Escolher Plano Pago
                                </b-button>
                            </div>
                            
                            <div class="mt-3">
                                <a href="https://wa.me/5511939078700?text=Olá! Gostaria de conhecer os planos da plataforma Lummefy." 
                                   target="_blank" 
                                   class="btn btn-outline-success">
                                    <i class="fab fa-whatsapp mr-2"></i>
                                    Falar com Especialista
                                </a>
                            </div>
                        </b-col>
                    </b-row>
                </b-card-body>

                <!-- Modal de Confirmação de Cancelamento -->
                <b-modal v-model="showCancelModal" title="Cancelar Assinatura" @ok="cancelSubscription" ok-variant="danger" ok-title="Confirmar Cancelamento" cancel-title="Fechar">
                    <p>Você tem certeza que deseja cancelar sua assinatura?</p>
                    <p class="text-danger">Esta ação não poderá ser desfeita e você perderá acesso aos benefícios do plano.</p>
                </b-modal>

                <!-- Modal de Reativação/Seleção de Plano -->
                <b-modal v-model="showReactivateModal" title="Escolher Plano" size="lg" @ok="reactivateSubscription" ok-title="Confirmar Plano" cancel-title="Fechar">
                    <div v-if="!selectedPlan">
                        <h6 class="mb-3">Escolha um plano:</h6>
                        <div class="row">
                            <div class="col-md-6 mb-3" v-for="plan in availablePlans" :key="plan.id">
                                <div class="card h-100" :class="{'border-primary': selectedPlanId === plan.id}">
                                    <div class="card-body text-center">
                                        <h6>{{ plan.title }}</h6>
                                        <p class="text-muted small" v-html="plan.description"></p>
                                        <h5 class="text-primary">R$ {{ formatCurrency(plan.value) }}</h5>
                                        <b-button 
                                            size="sm" 
                                            @click="selectPlan(plan)"
                                            :variant="selectedPlanId === plan.id ? 'primary' : 'outline-primary'"
                                        >
                                            {{ selectedPlanId === plan.id ? 'Selecionado' : 'Selecionar' }}
                                        </b-button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-else>
                        <h6 class="mb-3">Dados de Pagamento:</h6>
                        <b-form @submit.prevent="reactivateSubscription">
                            <b-row>
                                <b-col md="6">
                                    <b-form-group label="Nome do Titular:" label-for="holderName">
                                        <b-form-input 
                                            id="holderName" 
                                            v-model="paymentForm.holderName" 
                                            required
                                            placeholder="Nome do Titular do Cartão"
                                        ></b-form-input>
                                    </b-form-group>
                                </b-col>
                                <b-col md="6">
                                    <b-form-group label="CPF do Titular:" label-for="cpf">
                                        <b-form-input 
                                            id="cpf" 
                                            v-model="paymentForm.cpf" 
                                            required
                                            placeholder="000.000.000-00"
                                            v-maska="'###.###.###-##'"
                                        ></b-form-input>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col md="12">
                                    <b-form-group label="Número do Cartão:" label-for="number">
                                        <b-form-input 
                                            id="number" 
                                            v-model="paymentForm.number" 
                                            required
                                            placeholder="#### #### #### ####"
                                            v-maska="'#### #### #### ####'"
                                        ></b-form-input>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col md="6">
                                    <b-form-group label="Validade:" label-for="expiration">
                                        <b-form-input 
                                            id="expiration" 
                                            v-model="paymentForm.expiration" 
                                            required
                                            placeholder="mm/AAAA"
                                            v-maska="'##/####'"
                                        ></b-form-input>
                                    </b-form-group>
                                </b-col>
                                <b-col md="6">
                                    <b-form-group label="CVV:" label-for="ccv">
                                        <b-form-input 
                                            id="ccv" 
                                            v-model="paymentForm.ccv" 
                                            required
                                            placeholder="###"
                                            v-maska="'###'"
                                        ></b-form-input>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col md="6">
                                    <b-form-group label="Telefone:" label-for="phone">
                                        <b-form-input 
                                            id="phone" 
                                            v-model="paymentForm.phone" 
                                            required
                                            placeholder="(11) 99999-9999"
                                            v-maska="'(##) #####-####'"
                                        ></b-form-input>
                                    </b-form-group>
                                </b-col>
                                <b-col md="6">
                                    <b-form-group label="CEP:" label-for="postalCode">
                                        <b-form-input 
                                            id="postalCode" 
                                            v-model="paymentForm.postalCode" 
                                            required
                                            placeholder="00000-000"
                                            v-maska="'#####-###'"
                                        ></b-form-input>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col md="6">
                                    <b-form-group label="Número:" label-for="addressNumber">
                                        <b-form-input 
                                            id="addressNumber" 
                                            v-model="paymentForm.addressNumber" 
                                            required
                                            placeholder="123"
                                        ></b-form-input>
                                    </b-form-group>
                                </b-col>
                                <b-col md="6">
                                    <b-form-group label="Complemento:" label-for="addressComplement">
                                        <b-form-input 
                                            id="addressComplement" 
                                            v-model="paymentForm.addressComplement" 
                                            placeholder="Apto 101"
                                        ></b-form-input>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                        </b-form>
                    </div>
                </b-modal>

            </div>
        </b-card>
    </Layout>
</template>

<script>

import Layout from '@/components/layout/main.vue';
import http from '@/http'
import {endLoading, startLoading} from "@/components/composables/spinners";
import {toFormData, ValidateForm} from "@/components/composables/cruds";
import {handleImg, setImg} from "@/components/composables/img";

export default {
    components: {
        Layout
    },

    data() {
        return {
            user: {
                name: '',
                cpf: '',
                phone: '',
                email: '',
                profession: '',
                mini_resume: '',
                professional_experience: '',
                academic_education: '',
                instagram: '',
                photo_url: null,
                photo: null,
            },
            currentUser: this.$store.state.auth.user,
            currentPlan: null,
            availablePlans: [],
            selectedPlan: null,
            selectedPlanId: null,
            showCancelModal: false,
            showReactivateModal: false,
            loading: false,
            isSubscriptionActive: false,
            trialExpiresAt: null,
            paymentForm: {
                holderName: '',
                number: '',
                expiration: '',
                ccv: '',
                cpf: '',
                phone: '',
                postalCode: '',
                addressNumber: '',
                addressComplement: ''
            }
        }
    },

    methods: {
        setup() {
            this.getData();
            this.getPlan();
            this.getAvailablePlans();
        },

        formatCurrency(value) {
            if (!value) return '0,00';
            return parseFloat(value).toFixed(2).replace('.', ',');
        },

        formatDate(dateString) {
            if (!dateString) return null;
            const date = new Date(dateString);
            return date.toLocaleDateString('pt-BR');
        },

        getPlan() {
            if (!this.currentUser.plan_id) {
                this.isSubscriptionActive = false;
                return;
            }
            
            http.get('plans/' + this.currentUser.plan_id)
                .then(response => {
                    this.currentPlan = response.data.data[0];
                    this.isSubscriptionActive = true;
                    
                    // Se for trial, busca a data de expiração
                    if (this.currentPlan.type === 'TRIAL' && this.currentUser.trial_expires_at) {
                        this.trialExpiresAt = this.formatDate(this.currentUser.trial_expires_at);
                    }
                })
                .catch(e => {
                    console.error('Plano: ', e.response);
                    this.isSubscriptionActive = false;
                });
        },

        getAvailablePlans() {
            http.get('plans/list?paginated=false')
                .then(response => {
                    this.availablePlans = response.data.data;
                })
                .catch(e => {
                    console.error('Planos disponíveis: ', e.response);
                    this.$notifyError('Erro ao carregar planos disponíveis!');
                });
        },

        selectPlan(plan) {
            this.selectedPlan = plan;
            this.selectedPlanId = plan.id;
            
            // Se for trial, inicia diretamente sem pedir dados de pagamento
            if (plan.type === 'TRIAL') {
                this.startTrial();
                return;
            }
        },

        startTrial() {
            startLoading('form', 'save');
            this.$store.commit('api/setSpinner', true);

            // Usa o plano selecionado se disponível, senão busca o plano de trial
            let trialPlan = this.selectedPlan;
            
            if (!trialPlan) {
                trialPlan = this.availablePlans.find(plan => plan.type === 'TRIAL');
            }
            
            if (!trialPlan) {
                this.$notifyError('Plano de trial não encontrado!');
                endLoading('form', 'save');
                this.$store.commit('api/setSpinner', false);
                return;
            }

            http.post('asaas/start-trial', {
                plan_id: trialPlan.id
            })
            .then(response => {
                this.$notifySuccess(response.data.message);
                this.$store.dispatch('auth/refreshUser');
                this.showReactivateModal = false; // Fecha o modal se estiver aberto
                this.selectedPlan = null;
                this.selectedPlanId = null;
            })
            .then(() => {
                window.setTimeout(() => {
                    window.location.reload();
                }, 3000);
            })
            .catch(e => {
                console.error('Trial: ', e.response);
                this.$notifyError(e.response?.data?.message || 'Erro ao iniciar trial!');
            })
            .finally(() => {
                endLoading('form', 'save');
                this.$store.commit('api/setSpinner', false);
            });
        },

        reactivateSubscription() {
            if (!this.selectedPlanId) {
                this.$notifyError('Por favor, selecione um plano!');
                return;
            }

            if (this.selectedPlan) {
                // Valida dados de pagamento
                if (!this.paymentForm.holderName || !this.paymentForm.cpf || !this.paymentForm.phone || 
                    !this.paymentForm.number || !this.paymentForm.expiration || !this.paymentForm.ccv ||
                    !this.paymentForm.postalCode || !this.paymentForm.addressNumber) {
                    this.$notifyError('Por favor, preencha todos os dados de pagamento!');
                    return;
                }
            }

            startLoading('form', 'save');
            this.$store.commit('api/setSpinner', true);

            const paymentData = this.selectedPlan ? {
                billingType: 'CREDIT_CARD',
                cycle: 'MONTHLY',
                creditCard: {
                    holderName: this.paymentForm.holderName,
                    number: this.paymentForm.number.replace(/\s/g, ''),
                    expiryMonth: this.paymentForm.expiration.split('/')[0],
                    expiryYear: this.paymentForm.expiration.split('/')[1],
                    ccv: this.paymentForm.ccv
                },
                creditCardHolderInfo: {
                    name: this.currentUser.name,
                    email: this.currentUser.email,
                    cpfCnpj: this.paymentForm.cpf.replace(/\D/g, ''), // Remove formatação do CPF
                    phone: this.paymentForm.phone.replace(/\D/g, ''), // Remove formatação do telefone
                    postalCode: this.paymentForm.postalCode,
                    addressNumber: this.paymentForm.addressNumber,
                    addressComplement: this.paymentForm.addressComplement
                }
            } : null;

            http.post('asaas/reactivate-subscription', {
                plan_id: this.selectedPlanId,
                payment_data: paymentData
            })
            .then(response => {
                this.$notifySuccess(response.data.message);
                this.$store.dispatch('auth/refreshUser');
                this.showReactivateModal = false;
                this.selectedPlan = null;
                this.selectedPlanId = null;                
            })
            .then(() => {
                window.setTimeout(() => {
                    window.location.reload();
                }, 3000);
            })
            .catch(e => {
                console.error('Reativação: ', e.response);
                this.$notifyError(e.response?.data?.message || 'Erro ao ativar assinatura!');
            })
            .finally(() => {
                endLoading('form', 'save');
                this.$store.commit('api/setSpinner', false);
            });
        },

        cancelSubscription() {
            if (!this.currentPlan) {
                this.$notifyError('Não foi possível encontrar sua assinatura!');
                return;
            }
            
            startLoading('form', 'save');
            this.$store.commit('api/setSpinner', true);
            
            http.post("mentor/cancel-subscription")
                .then(() => {
                    this.$notifySuccess('Assinatura cancelada com sucesso!');
                    this.$store.dispatch('auth/refreshUser');
                    this.currentPlan = null;
                    this.isSubscriptionActive = false;
                    this.showCancelModal = false;
                })
                .catch(e => {
                    console.error('Cancelamento: ', e.response);
                    this.$notifyError('Erro ao cancelar a assinatura!');
                })
                .finally(() => {
                    endLoading('form', 'save');
                    this.$store.commit('api/setSpinner', false);
                });
        },

        async handlePerfil(event) {
            try {
                const {url, img} = await handleImg(event, 320, 320);
                this.user.photo = img;
                this.user.photo_url = url;
            } catch (error) {
                console.error("Erro ao carregar a imagem:", error);
            }
        },

        setImgPerfil(blob) {
            const {url, image} = setImg(blob);
            this.user.photo_url = url;
            this.user.photo = image;
        },

        save() {
            startLoading('form', 'save');
            this.$store.commit('api/setSpinner', true);
            const required = {
                name: 'Nome',
                email: 'E-mail',
                profession: 'Cargo/Profissão',
                mini_resume: 'Mini Currículo',
                professional_experience: 'Experiência Profissional',
                academic_education: 'Formação Acadêmica',
                cpf: 'CPF',
                phone: 'Telefone',
            };
            if (!ValidateForm(this.$root, this.user, required)) {
                endLoading('form', 'save');
                this.$store.commit('api/setSpinner', false);
                return;
            }

            let data = toFormData(this.user);

            data.append('id', this.currentUser.id);
            data.append('_method', 'PUT');

            const url = 'mentors/' + this.currentUser.id;

            http.post(url, data)
                .then(() => {
                    this.$store.dispatch('auth/refreshUser');
                    this.$notifySuccess('Dados atualizados com sucesso!');
                    this.$router.push('/home')
                })
                .catch(e => {
                    console.error('Mentor Data: ', e.response);
                    this.$notifyError(e);
                })
                .finally(() => {
                    endLoading('form', 'save');
                    this.$store.commit('api/setSpinner', false);
                })
        },

        getData() {
            this.loading = true;
            http.get('mentors/?id=' + this.currentUser.id)
                .then(response => {
                    this.user = response.data.data[0];
                })
                .catch(e => {
                    console.error('Mentor Data: ', e.response);
                    this.$notifyError('Erro ao carregar os dados do Mentor!');
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },

    mounted() {
        this.setup()
    }
}

</script>

<style scoped="true">
.mentor-image {
    border: white solid 3px;
    border-radius: 50%;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.card {
    transition: all 0.3s ease;
    cursor: pointer;
}

.card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.card.border-primary {
    border-width: 2px !important;
}

/* Estilos para Assinatura Ativa */
.subscription-active {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border: none;
    border-radius: 15px;
}

.subscription-header {
    padding: 2rem 0;
}

.subscription-status-badge {
    display: inline-flex;
    align-items: center;
    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 50px;
    font-weight: 600;
    font-size: 1.1rem;
    box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
}

.subscription-status-badge.trial-badge {
    background: linear-gradient(135deg, #ffc107 0%, #ffb300 100%);
    box-shadow: 0 4px 15px rgba(255, 193, 7, 0.3);
}

.subscription-status-badge i {
    font-size: 1.2rem;
}

.subscription-details {
    margin-top: 2rem;
}

.plan-card {
    background: white;
    border-radius: 20px;
    padding: 2rem;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    border: 1px solid #e9ecef;
    transition: all 0.3s ease;
}

.plan-card.trial-card {
    border: 2px solid #ffc107;
    background: linear-gradient(135deg, #fff8e1 0%, #fff3c4 100%);
}

.plan-icon.trial-icon {
    background: linear-gradient(135deg, #ffc107 0%, #ffb300 100%);
    box-shadow: 0 4px 15px rgba(255, 193, 7, 0.3);
}

.trial-pricing {
    text-align: center;
    background: linear-gradient(135deg, #fff8e1 0%, #fff3c4 100%);
    padding: 2rem;
    border-radius: 15px;
    margin-bottom: 2rem;
    border: 2px solid #ffc107;
}

.trial-label {
    color: #856404;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 0.5rem;
}

.trial-value {
    color: #28a745;
    font-size: 2.5rem;
    font-weight: 800;
    margin-bottom: 0.25rem;
}

.trial-period {
    color: #856404;
    font-size: 0.9rem;
}

.upgrade-btn {
    border-radius: 50px;
    padding: 0.75rem 2rem;
    font-weight: 600;
    transition: all 0.3s ease;
    border-width: 2px;
}

.upgrade-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(40, 167, 69, 0.3);
}

.plan-header {
    display: flex;
    align-items: center;
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 2px solid #f8f9fa;
}

.plan-icon {
    background: linear-gradient(135deg, #007bff 0%, #6610f2 100%);
    color: white;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 1.5rem;
    box-shadow: 0 4px 15px rgba(0, 123, 255, 0.3);
}

.plan-icon i {
    font-size: 1.5rem;
}

.plan-info {
    flex: 1;
}

.plan-title {
    color: #2c3e50;
    margin-bottom: 0.5rem;
    font-weight: 700;
}

.plan-description {
    color: #6c757d;
    line-height: 1.6;
}

.plan-features {
    margin-bottom: 2rem;
}

.feature-item {
    display: flex;
    align-items: center;
    margin-bottom: 1rem;
    padding: 0.75rem;
    background: #f8f9fa;
    border-radius: 10px;
    transition: all 0.3s ease;
}

.feature-item:hover {
    background: #e9ecef;
    transform: translateX(5px);
}

.feature-item i {
    margin-right: 1rem;
    font-size: 1.1rem;
    width: 20px;
}

.feature-item span {
    color: #495057;
    font-weight: 500;
}

.plan-pricing {
    text-align: center;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    padding: 2rem;
    border-radius: 15px;
    margin-bottom: 2rem;
    border: 2px solid #dee2e6;
}

.price-label {
    color: #6c757d;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 0.5rem;
}

.price-value {
    color: #28a745;
    font-size: 2.5rem;
    font-weight: 800;
    margin-bottom: 0.25rem;
}

.price-period {
    color: #6c757d;
    font-size: 0.9rem;
}

.plan-actions {
    text-align: center;
}

.cancel-btn {
    border-radius: 50px;
    padding: 0.75rem 2rem;
    font-weight: 600;
    transition: all 0.3s ease;
    border-width: 2px;
}

.cancel-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(220, 53, 69, 0.3);
}

/* Responsividade */
@media (max-width: 768px) {
    .plan-header {
        flex-direction: column;
        text-align: center;
    }
    
    .plan-icon {
        margin-right: 0;
        margin-bottom: 1rem;
    }
    
    .plan-card {
        padding: 1.5rem;
    }
    
    .price-value {
        font-size: 2rem;
    }
}

/* Estilos para Assinatura Inativa */
.subscription-inactive {
    background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
    border: none;
    border-radius: 15px;
    padding: 2rem;
}

.status-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.status-icon.warning {
    background: linear-gradient(135deg, #ffc107 0%, #ffb300 100%);
    color: white;
}

.status-icon.info {
    background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
    color: white;
}

.status-icon i {
    font-size: 2rem;
}

.reactivate-btn, .choose-plan-btn {
    border-radius: 50px;
    padding: 1rem 2.5rem;
    font-weight: 600;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.reactivate-btn:hover, .choose-plan-btn:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

/* Estilos para Sem Plano */
.subscription-none {
    background: linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%);
    border: none;
    border-radius: 15px;
    padding: 2rem;
}

/* Estilos para Trial */
.trial-option {
    margin-bottom: 2rem;
}

.trial-card {
    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
    border-radius: 20px;
    padding: 2rem;
    color: white;
    box-shadow: 0 10px 30px rgba(40, 167, 69, 0.3);
    transition: all 0.3s ease;
    border: 3px solid transparent;
}

.trial-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(40, 167, 69, 0.4);
    border-color: rgba(255, 255, 255, 0.3);
}

.trial-header {
    display: flex;
    align-items: center;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 2px solid rgba(255, 255, 255, 0.2);
}

.trial-icon {
    background: rgba(255, 255, 255, 0.2);
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 1.5rem;
    backdrop-filter: blur(10px);
}

.trial-icon i {
    font-size: 1.8rem;
    color: white;
}

.trial-info {
    flex: 1;
}

.trial-title {
    color: white;
    margin-bottom: 0.25rem;
    font-weight: 700;
    font-size: 1.5rem;
}

.trial-subtitle {
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 0;
    font-size: 1rem;
}

.trial-description {
    margin-bottom: 2rem;
    text-align: center;
}

.trial-description p {
    color: rgba(255, 255, 255, 0.95);
    font-size: 1.1rem;
    margin-bottom: 0;
    line-height: 1.6;
}

.trial-btn {
    border-radius: 50px;
    padding: 1rem 2.5rem;
    font-weight: 600;
    transition: all 0.3s ease;
    background: white;
    color: #28a745;
    border: 2px solid white;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.trial-btn:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
    background: rgba(255, 255, 255, 0.95);
    color: #28a745;
}

.divider {
    position: relative;
    text-align: center;
    margin: 2rem 0;
}

.divider::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, #dee2e6, transparent);
}

.divider span {
    background: #f8f9fa;
    padding: 0 1rem;
    color: #6c757d;
    font-weight: 500;
    position: relative;
    z-index: 1;
}

.paid-plan-option {
    margin-top: 1rem;
}
</style>

<style>
ul.benifits {
    list-style-type: none !important;
    padding-left: 0px !important;
}
</style>
